import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, EMPTY, merge, NEVER, Observable, throwError } from 'rxjs';
import {
  distinctHydraMember,
  ErrorHandlerStrategy,
  filterUndefined,
  Professional,
  ProfessionalJob,
  ProfessionalPrivate,
  ProfessionalPrivateService,
  ProfessionalService,
} from '@adeo/ngx-kozikaza-api';
import { Metas, SeoService } from '../../../shared/services/utilities/seo.service';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { UserStoreService } from '../../../shared/services/state-management/user-store.service';
import { catchError, filter, map, shareReplay, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { navigationUserProfessionalSubResources } from '../../../utils/const/pro';
import { StringUtilities } from '../../../utils/misc/string-utilities';
import { CountryStaticService } from '../../../shared/services/utilities/country-static.service';
import { getTranslation } from '../../../utils/const/translation';
import { Country } from '@adeo/ngx-kozikaza-api/misc/country';

@Injectable()
export class ProNavigationService {

    private job$: BehaviorSubject<ProfessionalJob> = new BehaviorSubject<ProfessionalJob>(undefined);
    private proProfile$: BehaviorSubject<Professional> = new BehaviorSubject<Professional>(undefined);
    private proProfilePrivate$: BehaviorSubject<ProfessionalPrivate> = new BehaviorSubject<ProfessionalPrivate>(undefined);
    private proProfileUpdate$: BehaviorSubject<Professional> = new BehaviorSubject<Professional>(undefined);
    private forceProProfileUpdate$: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
    private countries$: Observable<Array<Country>>;

    constructor(
        private activatedRouteB: ActivatedRoute,
        private seoService: SeoService,
        private router: Router,
        private userStoreService: UserStoreService,
        private professionalService: ProfessionalService,
        private professionalPrivateService: ProfessionalPrivateService,
        private countryService: CountryStaticService,
    ) {
        this.countries$ = this.countryService.getCountries().pipe(
            map((countries) => countries['hydra:member']),
            shareReplay(1)
        );
        combineLatest([
            merge(
                this.forceProProfileUpdate$.asObservable().pipe(
                    filter((force) => !!force),
                    switchMap((forcedPro) => {
                        return (forcedPro) ?
                            this.professionalService.getProfessional(forcedPro, navigationUserProfessionalSubResources, {refresh: false,
                                errorHandlerStrategy: ErrorHandlerStrategy.Ignore}).pipe(filter((profilePro) => !!profilePro))
                            : EMPTY;
                    })
                ),
                this.proProfileUpdate$.asObservable().pipe(filter((update) => !!update))
            ),
            this.userStoreService.userStore.pipe(
                filterUndefined(),
                distinctHydraMember(),
            )
        ]).pipe(
            map(([profilePro, user]) => ({profilePro: profilePro as Professional, userLogged: user })),
            switchMap((result) => {
                /* If ProProfile has been updated or UserStore has changed, check ProfessionalPrivate */
                const profilePro = result.profilePro;
                const userLogged = result.userLogged;
                const authorId = profilePro && profilePro.socialKozikazaUserId ? typeof profilePro.socialKozikazaUserId === 'string' ?
                    profilePro.socialKozikazaUserId : profilePro.socialKozikazaUserId['@id'] : null;
                const isAuthorOrAdmin = this.userStoreService.isAdmin || ((authorId && userLogged && userLogged['@id']) ?
                    authorId === userLogged['@id'] : false);
                this.proProfile$.next(profilePro);
                if (!isAuthorOrAdmin) {
                    this.proProfilePrivate$.next(null);
                    return NEVER;
                } else {
                    return this.professionalPrivateService.getProfessionalPrivateByProfessional(profilePro).pipe(
                        catchError((err) => {
                            this.proProfilePrivate$.next(null);
                            return throwError(err);
                        }),
                        tap((profileProPrivate) => this.proProfilePrivate$.next(profileProPrivate))
                    );
                }
            }),
        ).subscribe();
    }

    /*
    * Pro Job
    * */
    public get job(): Observable<ProfessionalJob> {
        return this.job$.asObservable();
    }

    public setJob(job: ProfessionalJob, setMetas = true): void {
        this.job$.next(job);
        if (setMetas) {
            this.initMetas(this.getMetasJob(job));
        }
    }

    /*
    * Pro
    * */
    public get proProfile(): Observable<Professional> {
        return this.proProfile$.asObservable().pipe(filterUndefined());
    }

    public get proProfileValue(): Professional {
        return this.proProfile$.getValue();
    }

    public setProfile(profile: Professional, setMetas = true): void {
        // if one of userProfessional's sub-resources is not populated, then call forceUserProfessionalStoreUpdate$
        const needForceUpdateSubRessources = profile && navigationUserProfessionalSubResources.some((sub) =>
            (typeof sub === 'string' && profile[sub] && typeof profile[sub] === 'string'));
        // needForceUpdateSubRessources ? this.forceProProfileUpdate$.next(profile['@id']) : this.proProfile$.next(profile);
        needForceUpdateSubRessources ? this.forceProProfileUpdate$.next(profile['@id']) : this.proProfileUpdate$.next(profile);
        if (setMetas) {
            this.initMetas(this.getMetasProfessional(profile));
        }
    }

    /*
      * Pro Private
      * */
    public get proProfilePrivate(): Observable<ProfessionalPrivate> {
        return this.proProfilePrivate$.asObservable().pipe(filterUndefined());
    }

    public get proProfilePrivateValue(): ProfessionalPrivate {
        return this.proProfilePrivate$.getValue();
    }

    public setProfilePrivate(professionalPrivate: ProfessionalPrivate): void {
        this.proProfilePrivate$.next(professionalPrivate);
    }

    /*
    * Current user is Owner
    * */
    public isOwner(): Observable<boolean> {
        return combineLatest([
            this.proProfile.pipe(filterUndefined()),
            this.userStoreService.userStore.pipe(filterUndefined())
        ]).pipe(
            map(([profile, userLogged]) => {
                const authorId = profile && profile.socialKozikazaUserId ? typeof profile.socialKozikazaUserId === 'string' ?
                    profile.socialKozikazaUserId : profile.socialKozikazaUserId['@id'] : null;
                const isAuthor = (authorId && userLogged && userLogged['@id']) ? authorId === userLogged['@id'] : false;
                return isAuthor;
            })
        );
    }

    /*
    * Pro is KZKZ community
    * */
    public get proIsCommunity(): Observable<boolean> {
        return this.proProfile.pipe(
            withLatestFrom(this.countries$),
            map(([pro, countries]) => {
                const proCountry = (typeof pro.socialKozikazaKazaId !== 'string' && pro.socialKozikazaKazaId.country) ?
                    pro.socialKozikazaKazaId.country : null;
                if (proCountry) {
                    const proIsCommunity = countries.findIndex((country) => country.iso === proCountry) >= 0;
                    return proIsCommunity;
                }
                // by default, pro without country is community.
                return true;
            })
        );
    }

    /*
    * Pro Metas
    * */
    public initMetas(metas: Metas, activatedRoute?: ActivatedRouteSnapshot, initMetasFromRoute = false): Metas {
        const route = (activatedRoute) ? activatedRoute : this.activatedRoute;
        if (!initMetasFromRoute) {
            this.seoService.setMetas({ ...(route && route.data && route.data.metas ? route.data.metas : []) , ...metas});
        }
        return metas;
    }

    public getMetasJob(job: ProfessionalJob, activatedRoute?: ActivatedRouteSnapshot): Metas {
        const route = (activatedRoute) ? activatedRoute : this.activatedRoute;
        const titleKey = (job && route.data && route.data.metas && route.data.metas.titleKey) ?
            route.data.metas.titleKey : 'meta.pro.job.title';
        const title = job ? getTranslation(titleKey, {jobName : job.name} ) : null;
        // todo : fill metas with API when available
        const desc = ``;
        const image = ``;
        return {title, description : desc, image};
    }

    public getMetasProfessional(pro: Professional, activatedRoute?: ActivatedRouteSnapshot): Metas {
        const route = (activatedRoute) ? activatedRoute : this.activatedRoute;
        const titleKey = (pro && route.data && route.data.metas && route.data.metas.titleKey) ?
            route.data.metas.titleKey : 'meta.pro.posts-flux.title';
        const job = this.job$.getValue();
        const title = pro ?
            ((!pro.city) ?
                getTranslation(`${titleKey}WithoutCity`,
                    {jobName : job ? job.name : '', proName: pro.brand} ) :
                getTranslation(titleKey,
                    {jobName : job ? job.name : '', proName: pro.brand, city: pro[`city`]} ))
            : null;
        const desc = pro?.description ? `${StringUtilities.shorten(pro.description, 120, '...', true)}` : null ;
        const image = pro?.avatar?.contentUrl ? `${pro.avatar.contentUrl}` : null;
        return {title, description : desc, image};
    }

    /*
    * Misc
    * */
    private get activatedRoute(): ActivatedRouteSnapshot {
        let route = this.router.routerState.root;
        while (route.firstChild) {
            route = route.firstChild;
        }
        return route.snapshot;
    }
}
